"use client";

import Image from "next/image";
import { ReactNode } from "react";
import { cn } from "@punchup/utils/cn";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { useVenuePageTheme } from "@punchup/venues";

interface VenueLogoPropsBase {
  /* Required props */
  venueSlug: string;
  logoImg: string | null;
  venueName: string;
  /* Optional props */
  className?: string;
  imageClassName?: string;
  /**
   * Custom placeholder content when logoImg is null.
   * If not provided, renders an empty themed container.
   */
  placeholderContent?: ReactNode;
}

interface VenueLogoPropsWithDimensions extends VenueLogoPropsBase {
  fill?: false;
  imageWidth?: number;
  imageHeight?: number;
}

interface VenueLogoPropsWithFill extends VenueLogoPropsBase {
  fill: true;
  imageWidth?: never;
  imageHeight?: never;
}

type VenueLogoProps = VenueLogoPropsWithDimensions | VenueLogoPropsWithFill;

/**
 * A venue logo image that applies the background color from the venue's theme.
 * Supports both fixed dimensions (imageWidth/imageHeight) and fill mode.
 * When logoImg is null, displays a placeholder with the themed background.
 */
export default function VenueLogo({
  venueSlug,
  logoImg,
  venueName,
  className,
  imageClassName,
  fill,
  imageWidth = 100,
  imageHeight = 100,
  placeholderContent,
}: VenueLogoProps) {
  const theme = useVenuePageTheme(venueSlug);

  // if this venue has no logo image, return a placeholder
  if (!logoImg) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center aspect-square p-2 rounded-lg",
          className
        )}
        style={{
          backgroundColor: theme.logoImageBackgroundColor,
        }}
      >
        {placeholderContent}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center aspect-square p-2 rounded-lg",
        className
      )}
      style={{
        backgroundColor: theme.logoImageBackgroundColor,
      }}
    >
      {fill ? (
        <Image
          src={getCDNImageURL(logoImg)}
          alt={venueName}
          fill
          sizes="100px"
          className={cn(
            "w-auto h-8 aspect-square object-contain",
            imageClassName
          )}
        />
      ) : (
        <Image
          src={getCDNImageURL(logoImg)}
          alt={venueName}
          width={imageWidth}
          height={imageHeight}
          sizes={`${Math.max(imageWidth, imageHeight)}px`}
          className={cn(
            "w-auto h-8 aspect-square object-contain",
            imageClassName
          )}
        />
      )}
    </div>
  );
}
